import React, { useState, useEffect } from 'react';
import { useSplash } from '../../../contexts/SplashContext';
import CommonInput from '../../Common/CommonInput';
import CommonSelect from '../../Common/CommonSelect';
import CommonCheckbox from '../../Common/CommonCheckbox';

import { fetchSettings } from '../../../store/modules/adminuser/adminuserActions';
import useFormSettings from '../../../hooks/useFormSettings';

const AdminuserForm = ({ initialData, onSave, isLoading, onClose }) => {
    const { settings, isLoadingSettings, isSettingsInitialized } = useFormSettings('adminuser', fetchSettings);
    const { roles, branches } = settings || {};
    const splashData = useSplash();

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: null,
        branches: [],
        password: '',
        password_confirmation: '',
        is_admin: true, // Default to true since role starts empty
    });
    
    const [errors, setErrors] = useState({});
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                name: initialData.name || '',
                email: initialData.email || '',
                role: initialData.role || null,
                branches: initialData.branches || [],
                password: initialData.password || '',
                password_confirmation: initialData.password_confirmation || '',
                // Set is_admin based on whether role is provided
                is_admin: !initialData.role || initialData.role === '' || initialData.role === null
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSaving(true);
            await onSave(formData);
            setErrors({});
            onClose();
        } catch (error) {
            if (error.errors) {
                const newErrors = {};
                Object.keys(error.errors).forEach(key => {
                    newErrors[key] = Array.isArray(error.errors[key]) 
                        ? error.errors[key][0] 
                        : error.errors[key];
                });
                setErrors(newErrors);
            } else {
                setErrors({
                    name: error.message || 'An error occurred while saving'
                });
            }
            return;
        } finally {
            setIsSaving(false);
        }
    };

    const isSubmitting = isLoading || isSaving;

    const handleSelectChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // Set is_admin to true when role is null/empty, false when role is selected
            is_admin: value === null || value === '' || value === undefined
        }));
        if (errors[name]) {
            setErrors(prev => ({...prev, [name]: ''}));
        }
    };

    if (isLoadingSettings || !isSettingsInitialized) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="h-100 d-flex flex-column">
            <div className="flex-grow-1">

                <div className="row">
                    <CommonInput
                        name="name"
                        label="Full Name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter full name"
                        disabled={isSubmitting}
                        errors={errors}
                        required={true}
                    />
                </div>
                <div className="row">
                    <CommonInput
                        name="email"
                        label="Email Address"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter email address"
                        disabled={isSubmitting}
                        errors={errors}
                        required={true}
                    />
                </div>
                <div className="row">
                    <CommonInput
                        name="password"
                        label="Password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter password"
                        disabled={isSubmitting}
                        errors={errors}
                    />
                </div>
                <div className="row">
                    <CommonInput
                        name="password_confirmation"
                        label="Confirm Password"
                        value={formData.password_confirmation}
                        onChange={handleChange}
                        placeholder="Enter confirm password"
                        disabled={isSubmitting}
                        errors={errors}
                    />
                </div>
                <div className="row">
                    <CommonSelect
                        name="branches"
                        label="Branches"
                        value={formData.branches}
                        onChange={(value) => handleSelectChange('branches', value)}
                        options={branches ? branches.map(branch => ({
                            id: branch.id,
                            label: branch.label
                        })) : []}
                        isSubmitting={isSubmitting}
                        errors={errors}
                        required={true}
                        isMulti={true}
                        placeholder="Select branches..."
                    />
                </div>
                <div className="row">   
                    <CommonSelect
                        name="role"
                        label="Role"
                        value={formData.role}
                        onChange={(value) => handleSelectChange('role', value)}
                        options={roles ? roles : []}
                        isSubmitting={isSubmitting}
                        errors={errors}
                        required={true}
                        placeholder="Select role..."
                    />
                </div>
                <div className="row">
                    <div className="mb-3">
                        <div className="border border-2 rounded-2 p-4 ">
                             <CommonCheckbox
                                 name="is_admin"
                                 label="Is Admin?"
                                 value={formData.is_admin}
                                 onChange={handleChange}
                                 errors={errors}
                                 disabled={isSubmitting || formData.role !== ''}
                                 inline={true}
                             />
                            <p className="text-muted mt-2 mb-0">
                                {formData.role === null 
                                    ? 'Admin access is automatically enabled when no role is selected. This gives full access to all features without any limitations.'
                                    : 'Admin access is automatically disabled when a specific role is selected. The user will have permissions based on the selected role.'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-footer">
                <button 
                    type="button" 
                    className="btn btn-light" 
                    onClick={onClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmitting}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Saving...
                        </>
                    ) : 'Save'}
                </button>
            </div>
        </form>
    );
};

export default AdminuserForm; 